import React from 'react';
import { Helmet } from 'react-helmet-async';

const RotatingGlobe = () => (
  <div style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
    <style>{`
      @keyframes spin-slow { from { transform: rotateY(0deg) rotateX(15deg); } to { transform: rotateY(360deg) rotateX(15deg); } }
      @keyframes spin-mid  { from { transform: rotateY(0deg) rotateX(55deg); } to { transform: rotateY(360deg) rotateX(55deg); } }
      @keyframes spin-fast { from { transform: rotateY(0deg) rotateX(80deg); } to { transform: rotateY(360deg) rotateX(80deg); } }
      @keyframes pulse-dot { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.15); } }
      .ring {
        position: absolute;
        border-radius: 50%;
        border: 1.5px solid;
        top: 50%; left: 50%;
        transform-style: preserve-3d;
      }
      .ring-1 { width: 64px; height: 64px; margin: -32px; border-color: rgba(192,153,71,0.9); animation: spin-slow 6s linear infinite; }
      .ring-2 { width: 64px; height: 64px; margin: -32px; border-color: rgba(192,153,71,0.5); animation: spin-mid 4s linear infinite reverse; }
      .ring-3 { width: 64px; height: 64px; margin: -32px; border-color: rgba(192,153,71,0.25); animation: spin-fast 3s linear infinite; }
      .globe-dot { position: absolute; width: 10px; height: 10px; background: #C09947; border-radius: 50%; top: 50%; left: 50%; margin: -5px; animation: pulse-dot 2.5s ease-in-out infinite; box-shadow: 0 0 10px rgba(192,153,71,0.6); }
    `}</style>
    <div style={{ perspective: '200px', width: '100%', height: '100%', position: 'relative' }}>
      <div className="ring ring-1" />
      <div className="ring ring-2" />
      <div className="ring ring-3" />
      <div className="globe-dot" />
    </div>
  </div>
);

const gold = '#C09947';
const goldLight = 'rgba(192,153,71,0.12)';
const goldBorder = 'rgba(192,153,71,0.25)';

const CrosbyProposal = () => {
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    <div style={{ background: '#FAFAF8', minHeight: '100vh', fontFamily: "'Inter', 'Plus Jakarta Sans', system-ui, sans-serif", color: '#1a1a1a', padding: '1.5rem 1.5rem' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .fade-up { animation: fadeUp 0.6s ease forwards; }
        .fade-up-1 { animation-delay: 0.1s; opacity: 0; }
        .fade-up-2 { animation-delay: 0.2s; opacity: 0; }
        .fade-up-3 { animation-delay: 0.3s; opacity: 0; }
        .fade-up-4 { animation-delay: 0.4s; opacity: 0; }
      `}</style>

      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <div className="fade-up fade-up-1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          <img src="/agility hex fonts.png" alt="Agility" style={{ height: '68px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <RotatingGlobe />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1.6px', fontWeight: 500 }}>Proposal</div>
              <div style={{ fontSize: '13px', color: '#555', marginTop: '2px' }}>April 2026</div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: `linear-gradient(to right, ${gold}, transparent)`, marginBottom: '1.25rem', opacity: 0.5 }} />

        {/* Card */}
        <div className="fade-up fade-up-2" style={{ border: `1px solid ${goldBorder}`, borderRadius: '16px', overflow: 'hidden', background: '#fff', boxShadow: '0 4px 40px rgba(0,0,0,0.06)' }}>

          {/* Hero */}
          <div style={{ padding: '1.75rem 2.25rem 1.5rem', borderBottom: `1px solid ${goldBorder}`, background: 'linear-gradient(135deg, #fff 0%, #FDFCF8 100%)' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '1.4rem', flexWrap: 'wrap' }}>
              {[
                { label: 'Project-Based · Fixed Fee' },
                { label: 'GDPR++ Compliant' },
                { label: 'Mistral AI Powered' },
              ].map((b, i) => (
                <span key={i} style={{ fontSize: '11px', fontWeight: 500, padding: '4px 13px', borderRadius: '20px', background: goldLight, color: '#8a6a20', border: `1px solid ${goldBorder}`, letterSpacing: '0.3px' }}>
                  {b.label}
                </span>
              ))}
            </div>
            <h1 style={{ fontSize: '30px', fontWeight: 700, color: '#111', lineHeight: 1.2, marginBottom: '0.8rem', letterSpacing: '-0.4px' }}>
              AI-Powered Contract Review Platform
            </h1>
            <p style={{ fontSize: '15px', color: '#666', lineHeight: 1.75, fontWeight: 300, maxWidth: '600px' }}>
              A Crosby.ai-style hybrid legal automation platform combining Mistral AI agents with human lawyer oversight for fast, compliant contract review. Built for speed-to-signature, starting from Slack.
            </p>
          </div>

          {/* Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: `1px solid ${goldBorder}`, background: '#FDFCF8' }}>
            {[
              { val: '$8,000', lbl: 'Fixed fee' },
              { val: '3 months', lbl: 'Phase 1 delivery' },
              { val: '2 engineers', lbl: 'Dedicated team' },
              { val: '3 channels', lbl: 'Slack · Email · API' },
            ].map((m, i, arr) => (
              <div key={i} style={{ padding: '1.4rem 1.5rem', borderRight: i < arr.length - 1 ? `1px solid ${goldBorder}` : 'none' }}>
                <div style={{ fontSize: '21px', fontWeight: 700, color: gold, marginBottom: '4px', letterSpacing: '-0.3px' }}>{m.val}</div>
                <div style={{ fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 500 }}>{m.lbl}</div>
              </div>
            ))}
          </div>

          {/* Deliverables */}
          <div style={{ padding: '2rem 2.75rem', borderBottom: `1px solid ${goldBorder}` }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '1.4rem' }}>
              Phase 1 Deliverables
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                'Slack bot intake contracts submitted directly via Slack message or file drop, auto-triaged by document type',
                'Email parser + webhook full three-channel intake at launch (Slack, email, API trigger)',
                'Mistral AI review engine clause extraction, risk flagging, redline suggestions tuned per client playbook',
                'Lawyer review dashboard structured queue, inline edit, approve and sign-off workflow',
                'Client portal contract upload, real-time status tracking, redlined output delivery',
                'Client memory layer preferences and fallback positions stored per document type, auto-injected into future reviews',
                'GDPR++ architecture EU-hosted, consent logs, DPA-ready data handling, audit trail',
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '14px', color: '#444', lineHeight: 1.65 }}>
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: gold, marginTop: '8px', flexShrink: 0 }} />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* Credibility strip */}
            <div style={{ background: goldLight, border: `1px solid ${goldBorder}`, borderRadius: '10px', padding: '16px 20px', marginTop: '1.75rem', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              <div style={{ flexShrink: 0, marginTop: '2px' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7.5" stroke={gold} strokeWidth="1.2"/>
                  <path d="M5 8.5l2 2 4-4" stroke={gold} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={{ fontSize: '13.5px', color: '#555', lineHeight: 1.7 }}>
                <span style={{ color: '#1a1a1a', fontWeight: 600 }}>Why trust Agility on this?</span> We have built 100s of Slack automations including a production Slack Analyser that scans and processes channel activity at scale. Mistral AI is already live in one of our production client projects. This is not a new stack for us.
              </div>
            </div>
          </div>

          {/* Team */}
          <div style={{ padding: '2rem 2.75rem', borderBottom: `1px solid ${goldBorder}` }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '1.4rem' }}>
              Dedicated Phase 1 Team
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {[
                { init: 'E1', name: 'Engineer 1 AI & Backend', role: 'Mistral integration · contract parsing · API layer' },
                { init: 'E2', name: 'Engineer 2 Frontend & Integrations', role: 'Slack bot · client portal · lawyer dashboard' },
              ].map((member, i) => (
                <div key={i} style={{ background: '#FDFCF8', border: `1px solid ${goldBorder}`, borderRadius: '10px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: goldLight, border: `1.5px solid ${goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, color: gold, flexShrink: 0 }}>
                    {member.init}
                  </div>
                  <div>
                    <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#1a1a1a', marginBottom: '4px' }}>{member.name}</div>
                    <div style={{ fontSize: '12px', color: '#999', fontWeight: 400 }}>{member.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Questions */}
          <div className="fade-up fade-up-3" style={{ padding: '2rem 2.75rem', borderBottom: `1px solid ${goldBorder}` }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '1.4rem' }}>
              4 Questions Before We Lock Scope
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { label: 'Q1 Legal structure', text: 'Will your client have licensed lawyers in-house to review AI outputs, or does the platform need to support a third-party lawyer assignment flow? This determines how we architect the human-in-the-loop layer.' },
                { label: 'Q2 Jurisdiction & contract types', text: 'Which countries will this serve, and what contract types are primary NDAs, MSAs, DPAs, or others? GDPR scope and clause libraries differ significantly by jurisdiction.' },
                { label: 'Q3 Volume & business model', text: "What's the expected monthly contract volume at launch, and how does the client plan to monetise per document, subscription, or internal tool? This determines infra sizing and feature priority." },
                { label: 'Q4 Existing systems', text: 'Does the client already use a CRM, CLM, or document system (HubSpot, Salesforce, Ironclad, SharePoint)? Any existing integrations will affect Phase 1 scope and timeline.' },
              ].map((q, i) => (
                <div key={i} style={{ borderLeft: `2.5px solid ${gold}`, padding: '14px 18px', background: goldLight, borderRadius: '0 10px 10px 0' }}>
                  <div style={{ fontSize: '10.5px', fontWeight: 700, color: '#8a6a20', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>{q.label}</div>
                  <div style={{ fontSize: '14px', color: '#444', lineHeight: 1.65 }}>{q.text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="fade-up fade-up-4" style={{ padding: '1.5rem 2.75rem', background: '#FDFCF8', borderTop: `1px solid ${goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ fontSize: '12.5px', color: '#888', lineHeight: 1.7, maxWidth: '540px' }}>
              Phase 2 (quoted separately): negotiation simulation, counterparty agents, advanced playbook learning, multi-jurisdiction clause libraries. Pricing assumes standard scope confirmed by answers above.
            </div>
            <div style={{ fontSize: '12px', color: gold, fontWeight: 600, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>agilitytech.ai · 2026</div>
          </div>

        </div>

        {/* Bottom signature line */}
        <div style={{ height: '1px', background: `linear-gradient(to right, transparent, ${gold}, transparent)`, margin: '2.5rem 0 0', opacity: 0.4 }} />

      </div>
    </div>
    </>
  );
};

export default CrosbyProposal;
